import React, { useEffect } from 'react';
import { App, Typography } from 'antd';
import { Mic, MicOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { ConversationRole, VOICE_TURN_SEPARATOR, type QuestionType, type VoiceTurnTiming } from '@evaltick/shared';
import {
  useRealtimeVoice,
  VoicePhase,
  VoiceActivity,
  MicIssue,
  POST_ANSWERING_PHASES,
  CONNECTED_IDLE_PHASES,
  RECORDING_PHASES,
} from '@/hooks';
import { MIN_CHARS } from '@/components/Interview/AnswerInput';
import { ConversationEntryKind, type ConversationEntry } from '@/store/interviewStore';
import styles from './VoiceAnswerPanel.module.scss';

const { Text } = Typography;

interface VoiceAnswerPanelProps {
  assessmentId: string;
  /** Names the question being practised — the backend looks up its wording itself. */
  interviewTemplateItemId: string;
  disabled: boolean;
  /** From the package's `coachingEnabled` setting — false hides the resume-to-converse affordance once an answer is captured. */
  coachingEnabled: boolean;
  /** The question's type — picks the framework whose target length the coach is told at Stop. */
  questionType: QuestionType;
  /** Whether this question already has a scored response — connecting can then only open a discussion, never a fresh recording. */
  alreadyAnswered: boolean;
  /** This question's stored transcript — seeds the session on mount/reconnect and is kept in sync via onConversationChange. */
  conversation: ConversationEntry[];
  /** Mirrors every transcript change back into the store — the source of truth once this panel unmounts on a mode switch. */
  onConversationChange: (entries: ConversationEntry[]) => void;
  /**
   * Fired the instant Stop finalizes the initial answer — Stop **is** the
   * submit now, so the caller wires this straight into the existing
   * runEvaluate the typed AnswerInput already used.
   */
  onAnswerCaptured: (transcript: string, turnTimings: VoiceTurnTiming[]) => void;
  /** A voice request got a 410 (package revoked); the parent kicks the candidate out. */
  onRevoked: (err: unknown) => void;
  /** So the parent can gate "Next Question" on the phase actually visible to the candidate rather than the invisible evaluate call. */
  onPhaseChange?: (phase: VoicePhase) => void;
  onActivityChange?: (activity: VoiceActivity) => void;
  /** So the parent can unlock the mode switch (typing instead) while the mic is muted/unplugged or transcription keeps failing. */
  onInputTroubleChange?: (trouble: boolean) => void;
  onAudioLevelSource?: (getLevel: () => number) => void;
  /** Hands the parent a way to add server-built context (the answer's timing once scoring lands) to the live session. */
  onSessionContextSink?: (addSessionContext: (text: string) => void) => void;
}

const STATE_LABEL: Record<VoicePhase, string> = {
  [VoicePhase.Idle]: 'Click the mic to start your answer',
  [VoicePhase.Connecting]: 'Connecting…',
  [VoicePhase.Asking]: 'Your mic turns on when the question finishes',
  [VoicePhase.Answering]: "Click the mic when you've finished your answer",
  [VoicePhase.Finalizing]: 'Scoring answer…',
  // Unlike after Asking, the mic stays off when feedback ends.
  [VoicePhase.Feedback]: 'Your coach is giving feedback…',
  [VoicePhase.Reconnecting]: 'Reconnecting…',
  [VoicePhase.AnswerCaptured]: 'Click the mic to ask a follow-up question',
  // Hands-free: the coach replies when they stop talking; clicking only pauses.
  [VoicePhase.Conversing]: 'Ask away — click the mic to pause',
  [VoicePhase.Paused]: 'Click the mic to ask a follow-up question',
  [VoicePhase.Suspended]: 'Click the mic to start your answer',
  // Not "Connection error": a blocked or missing mic lands here too.
  [VoicePhase.Error]: 'Something went wrong — see below',
};

const TRANSIENT_PHASES: ReadonlySet<VoicePhase> = new Set([
  VoicePhase.Connecting,
  VoicePhase.Reconnecting,
  // The question can't be cut short, so the button stays inert until it ends.
  VoicePhase.Asking,
  VoicePhase.Finalizing,
  VoicePhase.Feedback,
]);

const ACTION_LABEL: Record<VoicePhase, string> = {
  [VoicePhase.Idle]: 'Start',
  [VoicePhase.Suspended]: 'Start',
  [VoicePhase.Error]: 'Retry',
  [VoicePhase.Connecting]: 'Connecting…',
  [VoicePhase.Reconnecting]: 'Reconnecting…',
  [VoicePhase.Asking]: 'Listening…',
  [VoicePhase.Answering]: 'Stop & Submit',
  [VoicePhase.Finalizing]: 'Scoring…',
  [VoicePhase.Feedback]: 'Listening…',
  [VoicePhase.AnswerCaptured]: 'Ask a question',
  [VoicePhase.Conversing]: 'Pause',
  [VoicePhase.Paused]: 'Ask a question',
};

// Muted only informs: it clears on unmute, and Stop keeps working (the signal
// can be wrong). Disconnected can't recover on its own, so the mic button
// restarts the mic and call. Both unlock the mode switch.
const MIC_ISSUE_LABEL: Record<MicIssue, string> = {
  [MicIssue.Muted]: 'Your browser says the microphone is muted — check your system settings',
  [MicIssue.Disconnected]: 'Your microphone was disconnected — plug it back in, then click to restart it',
};
const COACH_SPEAKING_MIC_OFF_LABEL = 'Mic off while the coach speaks — click to pause';
const MIC_DISCONNECTED_ACTION_LABEL = 'Restart mic';
// Error's own label stays 'Retry' — a denied or missing mic isn't a connection.
const CONNECTION_LOST_ACTION_LABEL = 'Reconnect';

// A bare "ask a follow-up" didn't tell testers there was anyone to ask.
const FOLLOW_UP_EXAMPLES_HINT = 'like "Why didn\'t that part land?" or "What should I focus on next time?"';

const ALREADY_ANSWERED_LABEL = 'Already answered — click the mic to ask a follow-up question';

// Same state with coaching off: a statement, not an invitation, since the mic
// is hidden and there's no conversation to open.
const ANSWER_RECORDED_LABEL = 'Already answered';

// Idle and Suspended are the only phases whose label is decided before
// connect() asks the server; every other phase already reflects reality.
const PRE_CONNECT_PHASES: ReadonlySet<VoicePhase> = new Set([VoicePhase.Idle, VoicePhase.Suspended]);
const StopIcon: React.FC = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor">
    <rect x="7" y="7" width="10" height="10" rx="2" />
  </svg>
);

// What Stop would submit if clicked right now: the same Answer-kind candidate
// turns useRealtimeVoice's assembleAnswer collects, plus the turn still being
// transcribed. Only an estimate of the final text — the grace window after Stop
// can still add a trailing turn — but enough to tell a real answer from a
// too-short one before the submission becomes irreversible.
const answerSoFar = (entries: ConversationEntry[]): string =>
  entries
    .filter((e) => e.kind === ConversationEntryKind.Answer && e.role === ConversationRole.Candidate)
    .map((e) => e.content)
    .join(VOICE_TURN_SEPARATOR)
    .trim();

const TOO_SHORT_TITLE = "Keep going — that's too short to submit";
const TOO_SHORT_DESCRIPTION = "Once you stop, this becomes your final answer for this question and can't be re-recorded.";
// Stable key so hammering Stop refreshes the one notice instead of stacking copies.
const TOO_SHORT_NOTIFICATION_KEY = 'voice-answer-too-short';

export const VoiceAnswerPanel: React.FC<VoiceAnswerPanelProps> = ({
  assessmentId,
  interviewTemplateItemId,
  disabled,
  coachingEnabled,
  questionType,
  alreadyAnswered,
  conversation,
  onConversationChange,
  onAnswerCaptured,
  onRevoked,
  onPhaseChange,
  onActivityChange,
  onInputTroubleChange,
  onAudioLevelSource,
  onSessionContextSink,
}) => {
  const { notification } = App.useApp();

  const {
    phase,
    activity,
    errorMessage,
    entries,
    toggle,
    getRemoteAudioLevel,
    micIssue,
    notice,
    transcriptionFailing,
    connectionLost,
    reconnect,
    addSessionContext,
  } = useRealtimeVoice({
    assessmentId,
    interviewTemplateItemId,
    coachingEnabled,
    questionType,
    initialEntries: conversation,
    onEntriesChange: onConversationChange,
    onAnswerCaptured,
    onRevoked,
  });

  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);

  useEffect(() => {
    onInputTroubleChange?.(micIssue !== null || transcriptionFailing);
  }, [micIssue, transcriptionFailing, onInputTroubleChange]);

  useEffect(() => {
    onActivityChange?.(activity);
  }, [activity, onActivityChange]);

  useEffect(() => {
    onAudioLevelSource?.(getRemoteAudioLevel);
  }, [getRemoteAudioLevel, onAudioLevelSource]);

  useEffect(() => {
    onSessionContextSink?.(addSessionContext);
  }, [addSessionContext, onSessionContextSink]);

  // Stopping during Answering **is** submitting, and within this connection
  // it's one-way — the session moves to the follow-up phase and can't reopen
  // the answer. Below the bar AnswerInput's Submit uses — including nothing
  // at all — Stop doesn't go through; it just warns the candidate to keep
  // speaking (recording never pauses, so they stay aware it's still live),
  // so a too-short or empty transcript never reaches Evaluate. Every other
  // phase's Stop is just a pause, so it goes straight through.
  const handleMicClick = () => {
    // A disconnected mic is still in a recording phase, where toggle() means
    // Stop — which can't help a mic that's gone — so the button gets a fresh one.
    if (micDisconnected) {
      reconnect();
      return;
    }
    if (phase !== VoicePhase.Answering || answerSoFar(entries).length >= MIN_CHARS) {
      toggle();
      return;
    }

    notification.warning({
      key: TOO_SHORT_NOTIFICATION_KEY,
      message: TOO_SHORT_TITLE,
      description: TOO_SHORT_DESCRIPTION,
      classNames: { root: styles.tooShortNotice },
    });
  };

  const isRecording = RECORDING_PHASES.has(phase);
  const micOffForCoach = phase === VoicePhase.Conversing && activity === VoiceActivity.Speaking;
  const micDisconnected = micIssue === MicIssue.Disconnected;
  const showStopIcon = isRecording && !micDisconnected;
  const isTransient = TRANSIENT_PHASES.has(phase);
  const isConnectedIdle = CONNECTED_IDLE_PHASES.has(phase);

  // With coaching off, every phase from Stop onward has nothing left the mic
  // could ever do for this question — no spoken feedback, no resuming to
  // converse — so it (and its label) disappear the instant Stop is clicked,
  // not just once settled into AnswerCaptured/Paused. The main scroll area's
  // ScoringIndicator (SessionInterview.tsx) takes over as the one "still
  // working" signal instead of this panel's own label.
  // `alreadyAnswered` covers a resumed sitting starting at Idle, which no
  // post-answering phase catches, but where coaching-off should still hide
  // the mic since there's no discussion to open.
  const micHidden = !coachingEnabled && (POST_ANSWERING_PHASES.has(phase) || alreadyAnswered);
  const canToggle = !disabled && !isTransient && !micHidden;
  // Checked ahead of micHidden: this is the one state where the label outlives the mic.
  // A notice (reconnected mid-answer, transcription failing) replaces the label
  // while recording, until the candidate is heard again or stops.
  const stateLabel =
    alreadyAnswered && PRE_CONNECT_PHASES.has(phase)
      ? coachingEnabled
        ? ALREADY_ANSWERED_LABEL
        : ANSWER_RECORDED_LABEL
      : micHidden
        ? ''
        : micIssue
          ? MIC_ISSUE_LABEL[micIssue]
          : micOffForCoach
            ? COACH_SPEAKING_MIC_OFF_LABEL
            : notice && isRecording
            ? notice
            : STATE_LABEL[phase];

  // Emphasised because testers kept missing the follow-up chat.
  const inviteFollowUp =
    canToggle &&
    !micIssue &&
    (isConnectedIdle || (alreadyAnswered && PRE_CONNECT_PHASES.has(phase)));
  // Not in Paused: they've already found the chat.
  const showFollowUpExamples = inviteFollowUp && phase !== VoicePhase.Paused;

  // Live partial transcript — only the turn currently being spoken, not the
  // full history (that's rendered once-finalized by FeedbackThread below).
  const liveEntry = isRecording
    ? [...entries].reverse().find((e) => e.role === ConversationRole.Candidate && e.isStreaming)
    : undefined;

  return (
    <div className={styles.container}>
      <Text className={`${styles.stateLabel} ${inviteFollowUp ? styles.stateLabelInvite : ''}`}>{stateLabel}</Text>

      {!micHidden && (
        <>
          <div className={styles.micGlow}>
            <motion.button
              type="button"
              className={`${styles.micBtn} ${!canToggle ? styles.micBtnDisabled : ''}`}
              onClick={handleMicClick}
              disabled={!canToggle}
              whileHover={canToggle ? { scale: 1.05 } : {}}
              whileTap={canToggle ? { scale: 0.95 } : {}}
              aria-label={
                micDisconnected
                  ? 'Restart microphone'
                  : phase === VoicePhase.Answering
                  ? 'Stop and submit answer'
                  : phase === VoicePhase.Conversing
                    ? 'Pause conversation'
                    : inviteFollowUp
                      ? 'Ask a follow-up question'
                      : canToggle
                        ? 'Start voice practice'
                        : 'Mic disabled'
              }
            >
              {showStopIcon ? (
                <StopIcon />
              ) : canToggle ? (
                <Mic style={{ width: '1em', height: '1em' }} />
              ) : (
                <MicOff style={{ width: '1em', height: '1em' }} />
              )}
            </motion.button>
          </div>
          {!isTransient && (
            <Text className={styles.micActionLabel}>
              {micDisconnected
                ? MIC_DISCONNECTED_ACTION_LABEL
                : connectionLost
                  ? CONNECTION_LOST_ACTION_LABEL
                  : // Not ACTION_LABEL[phase]: a resumed answered question is Idle ("Start").
                    showFollowUpExamples
                    ? `${ACTION_LABEL[VoicePhase.AnswerCaptured]}, ${FOLLOW_UP_EXAMPLES_HINT}`
                    : ACTION_LABEL[phase]}
            </Text>
          )}
        </>
      )}

      {liveEntry && (
        <Text className={styles.transcriptPreview}>{liveEntry.content}</Text>
      )}

      {phase === VoicePhase.Error && errorMessage && (
        <div className={styles.errorRow}>
          <MicOff style={{ width: '1em', height: '1em' }} />
          <Text className={styles.errorText}>{errorMessage}</Text>
        </div>
      )}
    </div>
  );
};
